import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { canEditCrewDataAtOfficeReview } from '../core/crewOfficeEditAccess'
import { ToastContainer, toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import { CREWING_ACTION_PERMISSIONS, CREWING_READ_PERMISSIONS, useCrewingCanRead, useCrewingPermissionRevision } from '../core/crewingPermissionAccess'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import { canShowOvertimeStatusFilter, canViewCrewFinancialDrafts } from '../core/crewFinancialDraftAccess'
import { Company, CompanyAdmin, Crew, Vessel } from '../core/_models'
import {
  createCrewOvertime,
  deleteCrewOvertime,
  getCompanyAdminList,
  getCompanyList,
  getRanksforList,
  getCrewLatestBankingDetails,
  getCrewList,
  getCrewOvertimes,
  reviewCrewOvertime,
  submitCrewOvertime,
  CrewOvertime,
  OvertimeStatus,
  OvertimeWorkflowStage,
  updateCrewOvertime,
} from '../core/_requests'
import CrewOvertimeModal from '../components/CrewOvertimeModal'
import {getClaimRosterPeriod} from '../core/claimRosterPeriod'
import ViewCrewOvertimeModal from '../components/ViewCrewOvertimeModal'
import CrewOvertimeReviewModal from '../components/CrewOvertimeReviewModal'
import DeleteCrewOvertimeModal from '../components/DeleteCrewOvertimeModal'
import CrewOvertimeView2 from '../components/CrewOvertimeView2'
import { RemarksCell, RemarksModal } from '../../QHSE/components/StructuredRemarksView'
import {
  buildCrewSignOnDateMap,
  getCrewSignOnMonth,
  toSignOnMonthKey,
} from '../core/signOnDateUtils'
import {
  applyCrewingPresentationFilters,
  allowCompanyPresentationFilters,
  extractRows,
} from '../core/crewingPresentationFilters'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'

const round = (value: number, places = 2) => {
  const factor = 10 ** places
  return Math.round((Number(value || 0) + Number.EPSILON) * factor) / factor
}

const resolveCurrencyCode = (salaryCurrency?: string | null, salaryOtherCurrency?: string | null) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).formatToParts(0).find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const formatWithCurrencySymbol = (
  value: number,
  salaryCurrency?: string | null,
  salaryOtherCurrency?: string | null,
  places = 2
) => {
  const symbol = getCurrencySymbol(resolveCurrencyCode(salaryCurrency, salaryOtherCurrency))
  return `${symbol}${round(value, places).toLocaleString('en-IN')}`
}

const recalculate = (row: CrewOvertime): CrewOvertime => {
  const overtimeRate = Math.max(Number(row.overtimeRate ?? row.otPerHour ?? 0), 0)
  const maxOt = Math.max(Number(row.maxOtAllowed || 109), 0)
  const hours = Math.max(Number(row.otHours || 0), 0)
  const perHour = round(overtimeRate, 4)
  const perMonth = round(perHour * hours, 2)
  const amount = round(hours * perHour, 2)
  const exceedAmount = round(Math.max(hours - maxOt, 0) * perHour, 2)
  return {
    ...row,
    overtimeRate: round(overtimeRate, 4),
    otHours: round(hours, 2),
    maxOtAllowed: round(maxOt, 2),
    otPerMonth: perMonth,
    otPerHour: perHour,
    otAmount: amount,
    exceedAmount,
  }
}

const resolveWorkflowStage = (row: CrewOvertime): OvertimeWorkflowStage => {
  if (row.workflowStage === 'MASTER_REVIEW') return 'OFFICE_REVIEW'
  if (row.workflowStage) return row.workflowStage
  if (row.status === 'SUBMITTED') {
    return 'OFFICE_REVIEW'
  }
  if (row.status === 'REVIEWED') return 'CLOSED'
  return 'OFFICE_REVIEW'
}

const defaultStatusBadge = (row: CrewOvertime) => {
  switch (row.status) {
    case 'DRAFT':
      return <span className='badge badge-light-secondary'>Draft</span>
    case 'SUBMITTED':
      return <span className='badge badge-light-warning'>Submitted (Office Review)</span>
    case 'REVIEWED':
      return <span className='badge badge-light-success'>Reviewed</span>
    case 'REJECTED':
      return <span className='badge badge-light-danger'>Reverted</span>
    default:
      return <span className='badge badge-light'>{row.status}</span>
  }
}

type SortKey = 'srNo' | 'crewName' | 'rankName'

const normalizeRankName = (rank?: string | null) =>
  (rank || '').toString().toUpperCase().replace(/\s+/g, ' ').trim()

const normalizeRankRuleKey = (rank?: string | null) =>
  (rank || '').toString().toUpperCase().replace(/[^A-Z0-9]+/g, ' ').replace(/\s+/g, ' ').trim()

const EDIT_REVIEW_ONLY_CREW_RANKS = new Set<string>([
  'MASTER',
])

const FILL_OTHERS_ONLY_CREW_RANKS = new Set<string>([
  'MASTER',
])

const VIEW_ONLY_CREW_RANKS = new Set<string>([
  'CADET',
  'ELECTRICAL ENGINEER [ETO]',
  'JR ENG',
])

// Overtime eligibility is still enforced as a business rule by crew rank, separate from RBAC.
const isOvertimeBlockedRank = (rankName: string) => {
  if (!rankName) return false
  const normalized = normalizeRankRuleKey(rankName)
  if (!normalized) return false
  if (normalized.includes('MASTER')) return false

  return (
    normalized.includes('CHIEF OFFICER') ||
    normalized.includes('SECOND OFFICER') ||
    normalized.includes('2 O') ||
    normalized.includes('2ND OFF') ||
    normalized.includes('THIRD OFFICER') ||
    normalized.includes('3 O') ||
    normalized.includes('3RD OFF') ||
    normalized.includes('DECK CADET') ||
    normalized.includes('CHIEF ENGINEER') ||
    normalized.includes('SECOND ENGINEER') ||
    normalized.includes('2 E') ||
    normalized.includes('2 ENG') ||
    normalized.includes('THIRD ENGINEER') ||
    normalized.includes('3 E') ||
    normalized.includes('3 ENG') ||
    normalized.includes('FOURTH ENGINEER') ||
    normalized.includes('4 E') ||
    normalized.includes('4 ENG') ||
    normalized.includes('ELECTRICAL ENGINEER') ||
    normalized.includes('ELECTRICIAN') ||
    normalized.includes('ETO') ||
    normalized.includes('JR ENG')
  )
}

const isEditReviewOnlyRank = (rankName: string) => {
  if (!rankName) return false
  if (EDIT_REVIEW_ONLY_CREW_RANKS.has(rankName)) return true
  return rankName.includes('MASTER')
}

const isViewOnlyRank = (rankName: string) => {
  if (!rankName) return false
  if (VIEW_ONLY_CREW_RANKS.has(rankName)) return true
  return (
    rankName.includes('CADET') ||
    rankName.includes('ELECTRICAL ENGINEER') ||
    rankName.includes('ETO') ||
    rankName.includes('JR ENG')
  )
}

const isFillOthersOnlyRank = (rankName: string) => {
  if (!rankName) return false
  if (FILL_OTHERS_ONLY_CREW_RANKS.has(rankName)) return true
  return rankName.includes('MASTER')
}

const CrewOvertimePage: FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth()
  const {
    isCrew,
    isOfficeRole,
    isCompanyGroupAdmin,
    isCompanyAdmin: baseIsCompanyAdmin,
    isOperator,
  } = useCrewingRoleAccess()
  const roleId = currentUser?.role?.id
  const canReadOvertime = useCrewingCanRead(CREWING_READ_PERMISSIONS.OVERTIME)
  const permissionRevision = useCrewingPermissionRevision()

  const operatorCompanyAdminId = Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? 0) || undefined
  const isCompanyAdmin = baseIsCompanyAdmin || (isOperator && !!operatorCompanyAdminId)
  const crewRankName = normalizeRankName(
    (currentUser as any)?.rank?.rank ??
    (currentUser as any)?.rankName ??
    (currentUser as any)?.designation ??
    ''
  )
  const isEditReviewOnlyCrewRank = isCrew && isEditReviewOnlyRank(crewRankName)
  const isViewOnlyCrewRank = isCrew && isViewOnlyRank(crewRankName)
  const isFillOthersOnlyCrewRank = isCrew && isFillOthersOnlyRank(crewRankName)
  const isOvertimePageHiddenForCrew = isCrew && isOvertimeBlockedRank(crewRankName)
  const isRestrictedCrewRank = isEditReviewOnlyCrewRank || isViewOnlyCrewRank || isFillOthersOnlyCrewRank
  const hideStatusFilterForRole = !canShowOvertimeStatusFilter({isSuperAdmin, isCompanyGroupAdmin, isCompanyAdmin})
  const canViewDrafts = canViewCrewFinancialDrafts({isSuperAdmin, isOfficeRole})

  const canAddOvertime = isSuperAdmin || hasPermission('CREATE_CREW_OVERTIME') || hasPermission('CREATE_OVERTIME_ENTRY')
  const canEditOvertimeRows = isSuperAdmin || hasPermission('UPDATE_CREW_OVERTIME') || hasPermission('UPDATE_OVERTIME_ENTRY')
  const canDeleteOvertimeRows = isSuperAdmin || hasPermission('DELETE_CREW_OVERTIME') || hasPermission('DELETE_OVERTIME_ENTRY')
  const canOfficeStageReview = isSuperAdmin || (hasPermission('APPROVE_CREW_OVERTIME') && isOfficeRole)
  const canSubmitOvertime = isSuperAdmin || hasPermission('APPROVE_CREW_OVERTIME')
  const useCrewDropdownFilter = isSuperAdmin || isCompanyGroupAdmin || isCompanyAdmin || isRestrictedCrewRank
  const roleEntityId = Number((currentUser as any)?.roleEntityId ?? 0)
  const currentCrewId = isCrew ? Number((currentUser as any)?.roleEntityId ?? 0) : 0
  const currentCrewUsername = currentUser?.username ?? ''
  const isCrewMaster = isCrew && Number((currentUser as any)?.rank?.id ?? 0) === 1

  const fixedCompanyGroupId = useMemo(() => {
    if (isCompanyGroupAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? (roleEntityId || 0)) || undefined
    }
    if (isCompanyAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyAdmin?.cga?.id ?? 0) || undefined
    }
    return undefined
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, roleEntityId])

  const fixedCompanyAdminId = useMemo(() => {
    if (!isCompanyAdmin) return undefined
    return Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? (roleEntityId || 0)) || undefined
  }, [currentUser, isCompanyAdmin, roleEntityId])

  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, roleId, isOperator)

  const [rows, setRows] = useState<CrewOvertime[]>([])
  const [crewMembers, setCrewMembers] = useState<Crew[]>([])
  const [ranks, setRanks] = useState<{ id: number; rank: string }[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [statusFilter, setStatusFilter] = useState<'All' | OvertimeStatus>('All')
  const [monthFilter, setMonthFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('')
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'srNo',
    direction: 'asc',
  })
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  useEffect(() => {
    if (globalState.vessel?.id) {
      setSelectedVesselId(Number(globalState.vessel.id))
    } else {
      setSelectedVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setSelectedCompanyGroupId(Number(globalState.company.id))
        setSelectedCompanyAdminId('')
      } else {
        setSelectedCompanyAdminId(Number(globalState.company.id))
        setSelectedCompanyGroupId('')
      }
    } else {
      setSelectedCompanyGroupId('')
      setSelectedCompanyAdminId('')
    }
  }, [globalState.vessel, globalState.company])

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [activeView, setActiveView] = useState<'view1' | 'view2'>('view1')

  const [editing, setEditing] = useState<CrewOvertime | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [viewing, setViewing] = useState<CrewOvertime | null>(null)
  const [reviewing, setReviewing] = useState<CrewOvertime | null>(null)
  const [deleting, setDeleting] = useState<CrewOvertime | null>(null)
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })
  const crewFetchSeqRef = useRef(0)
  const [modalCrewMembers, setModalCrewMembers] = useState<Crew[]>([])
  const modalCrewFetchSeqRef = useRef(0)
  const modalRosterPeriod = getClaimRosterPeriod(editing?.overtimeMonth, monthFilter, new Date().toISOString())

  useEffect(() => {
    if (!modalOpen || !canReadOvertime) return
    const requestSeq = ++modalCrewFetchSeqRef.current
    setModalCrewMembers([])
    getCrewList({
      period: modalRosterPeriod,
      companyGroupId: fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined),
      companyAdminId: fixedCompanyAdminId ?? (selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined),
      vesselId: selectedVesselId ? Number(selectedVesselId) : undefined,
    }).then((crew) => {
      if (requestSeq === modalCrewFetchSeqRef.current) setModalCrewMembers(crew)
    }).catch((error) => {
      if (requestSeq === modalCrewFetchSeqRef.current) showErrorToast(error, 'Failed to fetch overtime crew', 'load')
    })
    return () => { modalCrewFetchSeqRef.current += 1 }
  }, [modalOpen, canReadOvertime, modalRosterPeriod, fixedCompanyGroupId, fixedCompanyAdminId,
    selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId])

  const rankNameById = useMemo(
    () => new Map<number, string>(ranks.map((rank) => [rank.id, normalizeRankName(rank.rank)])),
    [ranks]
  )

  const resolveCrewRankName = (crew?: Crew) => {
    if (!crew) return ''
    const directRank =
      (crew as any)?.rank?.rank ??
      (crew as any)?.rankName ??
      rankNameById.get(Number((crew as any)?.rankId ?? 0)) ??
      ''
    return normalizeRankName(directRank)
  }

  const isOvertimeTargetCrewAllowed = (crew?: Crew) => {
    if (!crew) return false
    const rankName = resolveCrewRankName(crew)
    return !isOvertimeBlockedRank(rankName) && !rankName.includes('MASTER')
  }

  const hydrateOvertimeRate = async (items: CrewOvertime[]) => {
    const crewIds = Array.from(
      new Set(
        items
          .map((row) => row.crewId)
          .filter((id) => Number(id) > 0)
      )
    )

    const detailsList = await Promise.all(
      crewIds.map(async (crewId) => {
        const details = await getCrewLatestBankingDetails(crewId)
        return [
          crewId,
          {
            overtimeRate: Number(details?.overtimeRate ?? details?.overtimePay ?? 0),
            salaryCurrency: details?.salaryCurrency ?? null,
            salaryOtherCurrency: details?.salaryOtherCurrency ?? null,
          },
        ] as const
      })
    )

    const detailsByCrew = new Map<number, { overtimeRate: number; salaryCurrency: string | null; salaryOtherCurrency: string | null }>(detailsList)

    return items.map((row) => {
      const resolvedRate = Number(row.overtimeRate ?? row.otPerHour ?? 0) > 0
        ? Number(row.overtimeRate ?? row.otPerHour ?? 0)
        : Number(detailsByCrew.get(row.crewId)?.overtimeRate ?? 0)

      const salaryCurrency = row.salaryCurrency ?? detailsByCrew.get(row.crewId)?.salaryCurrency ?? null
      const salaryOtherCurrency = row.salaryOtherCurrency ?? detailsByCrew.get(row.crewId)?.salaryOtherCurrency ?? null

      return recalculate({
        ...row,
        basicWages: Number(row.basicWages ?? 0),
        overtimeRate: resolvedRate,
        salaryCurrency,
        salaryOtherCurrency,
      })
    })
  }

  const fetchRows = async () => {
    setIsLoading(true)
    try {
      const apiRows = extractRows<CrewOvertime>(await getCrewOvertimes())
      const hydrated = await hydrateOvertimeRate(apiRows)
      setRows(canReadOvertime ? hydrated : [])
    } catch (error: any) {
      console.error('Error fetching crew overtime records:', error)
      showErrorToast(error, 'Failed to fetch crew overtime records', 'load')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCrewMembers = async (filters?: {
    companyGroupId?: number
    companyAdminId?: number
    vesselId?: number
  }) => {
    try {
      crewFetchSeqRef.current += 1
      const requestSeq = crewFetchSeqRef.current
      const data = await getCrewList({...filters, period: monthFilter || new Date().toISOString().slice(0, 7)})
      if (requestSeq !== crewFetchSeqRef.current) return
      setCrewMembers(data)
    } catch (error) {
      console.error('Error fetching crew members:', error)
      showErrorToast(error, 'Failed to fetch crew members', 'load')
    }
  }

  useEffect(() => {
    if (!roleId || isOvertimePageHiddenForCrew || !canReadOvertime) return
    fetchRows()
    getRanksforList().then(setRanks).catch((error) => showErrorToast(error, 'Failed to fetch ranks', 'load'))
    getCompanyList().then(setCompanies).catch((error) => showErrorToast(error, 'Failed to fetch companies', 'load'))
    getCompanyAdminList().then(setCompanyAdmins).catch((error) => showErrorToast(error, 'Failed to fetch sub-companies', 'load'))
    fetchAccessibleVesselsForCurrentUser().then((list) => setVessels(normalizeAccessibleVessels(list))).catch((error) => showErrorToast(error, 'Failed to fetch vessels', 'load'))
  }, [roleId, isOvertimePageHiddenForCrew, canReadOvertime, permissionRevision])

  useEffect(() => {
    if (!roleId || isOvertimePageHiddenForCrew || !canReadOvertime) return

    const filters: {
      companyGroupId?: number
      companyAdminId?: number
      vesselId?: number
    } = {
      companyGroupId: fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined),
      companyAdminId: fixedCompanyAdminId ?? (selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined),
      vesselId: selectedVesselId ? Number(selectedVesselId) : undefined,
    }

    fetchCrewMembers(filters)
  }, [
      roleId,
      isOvertimePageHiddenForCrew,
      canReadOvertime,
      permissionRevision,
      fixedCompanyGroupId,
      fixedCompanyAdminId,
      selectedCompanyGroupId,
      selectedCompanyAdminId,
      selectedVesselId,
      monthFilter,
    ])

const selfCrewMember = useMemo(() => {
  if (!isCrew) return undefined
  if (currentCrewId) {
    const byId = crewMembers.find((member) => member.id === currentCrewId)
    if (byId) return byId
  }
  if (!currentCrewUsername) return undefined
  return crewMembers.find((member) => member.username === currentCrewUsername)
}, [isCrew, currentCrewId, currentCrewUsername, crewMembers])

const lockCrewSelection = isCrew && !isCrewMaster && !isFillOthersOnlyCrewRank && !!selfCrewMember
const effectiveCurrentCrewId = Number(selfCrewMember?.id ?? currentCrewId ?? 0)

const overtimeEligibleCrewMembers = useMemo(
  () => crewMembers.filter((crew) => isOvertimeTargetCrewAllowed(crew)),
  [crewMembers, ranks]
)

const selectableCrewMembers = useMemo(() => {
  if (!isCrew) return overtimeEligibleCrewMembers
  if (isFillOthersOnlyCrewRank) {
    return overtimeEligibleCrewMembers.filter((crew) => crew.id !== effectiveCurrentCrewId)
  }
  if (selfCrewMember && isOvertimeTargetCrewAllowed(selfCrewMember)) return [selfCrewMember]
  return overtimeEligibleCrewMembers
}, [isCrew, isFillOthersOnlyCrewRank, overtimeEligibleCrewMembers, selfCrewMember, effectiveCurrentCrewId, ranks])

const modalEligibleCrewMembers = useMemo(() => modalCrewMembers.filter(isOvertimeTargetCrewAllowed), [modalCrewMembers, ranks])
const modalSelectableCrewMembers = useMemo(() => {
  if (!isCrew) return modalEligibleCrewMembers
  if (isFillOthersOnlyCrewRank) return modalEligibleCrewMembers.filter((crew) => crew.id !== effectiveCurrentCrewId)
  return modalEligibleCrewMembers.filter((crew) => crew.id === effectiveCurrentCrewId)
}, [modalEligibleCrewMembers, isCrew, isFillOthersOnlyCrewRank, effectiveCurrentCrewId])
const modalSignOnDateByCrewId = useMemo(() => buildCrewSignOnDateMap(modalCrewMembers), [modalCrewMembers])

const crewSignOnDateByCrewId = useMemo(
  () => buildCrewSignOnDateMap(crewMembers),
  [crewMembers]
)

// The API validates historical months against all service periods.
const handleMonthFilterChange = (value: string) => {
  setMonthFilter(value)
}

const resolveRankName = (rankId?: number | null) => {
  if (!rankId) return ''
  return ranks.find((rank) => rank.id === rankId)?.rank || ''
}

useEffect(() => {
  setCurrentPage(1)
}, [searchTerm, statusFilter, monthFilter, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId, selectedCrewId])

useEffect(() => {
  if (!hideStatusFilterForRole) return
  if (statusFilter !== 'All') {
    setStatusFilter('All')
  }
}, [hideStatusFilterForRole, statusFilter])

const vesselById = useMemo(() => new Map(vessels.map((v) => [v.id, v])), [vessels])

const ownVesselId = useMemo(() => {
  const value =
    (currentUser as any)?.vessel?.id ??
    selfCrewMember?.vesselId ??
    selfCrewMember?.vessel?.id ??
    0
  return Number(value) || undefined
}, [currentUser, selfCrewMember])

const ownVessel = useMemo(
  () => (ownVesselId ? vesselById.get(ownVesselId) : undefined),
  [ownVesselId, vesselById]
)

const ownCompanyAdminId = useMemo(() => {
  const value =
    (currentUser as any)?.companyAdminId ??
    (currentUser as any)?.companyAdmin?.id ??
    selfCrewMember?.companyAdminId ??
    ownVessel?.companyAdmin?.id ??
    (ownVessel as any)?.companyAdminId ??
    0
  return Number(value) || undefined
}, [currentUser, selfCrewMember, ownVessel])

const ownCompanyGroupId = useMemo(() => {
  const value =
    (currentUser as any)?.companyGroupAdminId ??
    (currentUser as any)?.companyGroupAdmin?.id ??
    (currentUser as any)?.companyAdmin?.cga?.id ??
    selfCrewMember?.companyGroupAdminId ??
    (ownVessel as any)?.companyGroupAdminId ??
    ownVessel?.companyGroupAdmin?.id ??
    (ownVessel?.companyAdmin as any)?.cgaid?.id ??
    ownVessel?.companyAdmin?.cga?.id ??
    0
  return Number(value) || undefined
}, [currentUser, selfCrewMember, ownVessel])

const isSelfOvertimeRow = (row: CrewOvertime) =>
  isCrew && effectiveCurrentCrewId > 0 && row.crewId === effectiveCurrentCrewId

const canActOnRow = (row: CrewOvertime) => {
  if (!isRestrictedCrewRank) return true
  return !isSelfOvertimeRow(row)
}

const canEditRow = (row: CrewOvertime) => {
  if (!canEditOvertimeRows) return false
  if (!canActOnRow(row)) return false
  return row.status === 'DRAFT' || (row.status === 'SUBMITTED' &&
    resolveWorkflowStage(row) === 'OFFICE_REVIEW' && canEditCrewDataAtOfficeReview(
      {isSuperAdmin, isCompanyAdmin: baseIsCompanyAdmin, isCompanyGroupAdmin, isOperator},
      canEditOvertimeRows, canOfficeStageReview
    ))
}

const canReviewRow = (row: CrewOvertime) => {
  if (row.status !== 'SUBMITTED') return false
  if (!canActOnRow(row)) return false
  return canOfficeStageReview
}

const filteredSubCompanies = useMemo(() => {
  const groupId = fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined)
  if (!groupId) return companies
  return companies.filter((company) => {
    const parentCompanyId =
      (company as any)?.cga?.id ??
      (company as any)?.cgaid?.id ??
      (company as any)?.companyGroupAdmin?.id ??
      (company as any)?.companyGroupAdminId
    return parentCompanyId === groupId
  })
}, [companies, selectedCompanyGroupId, fixedCompanyGroupId])

const filteredVessels = useMemo(() => {
  const adminId = fixedCompanyAdminId ?? (selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined)
  const groupId = fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined)
  const getVesselCompanyAdminId = (v: Vessel): number | undefined => v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined
  const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
    (v as any).companyGroupAdminId ?? v.companyGroupAdmin?.id ?? (v.companyAdmin as any)?.cgaid?.id ?? v.companyAdmin?.cga?.id

  if (adminId) return vessels.filter((v) => getVesselCompanyAdminId(v) === adminId)
  if (groupId) return vessels.filter((v) => getVesselCompanyGroupId(v) === groupId)
  return vessels
}, [vessels, selectedCompanyGroupId, selectedCompanyAdminId, fixedCompanyGroupId, fixedCompanyAdminId])

const showCrewFilter = !isCrew || isRestrictedCrewRank

const crewFilterOptions = useMemo(() => {
  const rowsToUse = selectableCrewMembers.length > 0 ? selectableCrewMembers : overtimeEligibleCrewMembers
  const uniqueById = new Map<number, Crew>()

  applyCrewingPresentationFilters(rowsToUse, {
    selectedVesselId,
    allowCompanyPresentationFilters: allowCompanyFilters,
    selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
    selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
  }, {
    vesselById,
    extraPredicate: (crew) => {
      const typed = crew
      if (!typed?.id || !isOvertimeTargetCrewAllowed(typed)) return false
      const vesselId = typed.vesselId ?? typed.vessel?.id
      const vessel = vesselId ? vesselById.get(vesselId) : undefined
      const companyGroupId = vessel
        ? ((vessel as any).companyGroupAdminId ??
          vessel.companyGroupAdmin?.id ??
          (vessel.companyAdmin as any)?.cgaid?.id ??
          vessel.companyAdmin?.cga?.id)
        : undefined
      const companyAdminId = vessel ? (vessel.companyAdmin?.id ?? (vessel as any).companyAdminId) : undefined
      const hasCrewScope = !!(ownCompanyGroupId || ownCompanyAdminId || ownVesselId)
      const matchesOwnCompanyGroup = !ownCompanyGroupId || companyGroupId === ownCompanyGroupId
      const matchesOwnCompanyAdmin = !ownCompanyAdminId || companyAdminId === ownCompanyAdminId
      const matchesOwnVessel = !ownVesselId || vesselId === ownVesselId
      return (
        !isRestrictedCrewRank ||
        ((!hasCrewScope || (matchesOwnCompanyGroup && matchesOwnCompanyAdmin && matchesOwnVessel)) &&
          typed.id !== effectiveCurrentCrewId)
      )
    },
  }).forEach((crew) => {
    uniqueById.set(Number(crew.id), crew as Crew)
  })

  return Array.from(uniqueById.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
}, [
  selectableCrewMembers,
  overtimeEligibleCrewMembers,
  selectedVesselId,
  vesselById,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  allowCompanyFilters,
  isRestrictedCrewRank,
  ownCompanyGroupId,
  ownCompanyAdminId,
  ownVesselId,
  effectiveCurrentCrewId,
])

useEffect(() => {
  if (!selectedCrewId) return
  const exists = crewFilterOptions.some((crew) => crew.id === Number(selectedCrewId))
  if (!exists) setSelectedCrewId('')
}, [selectedCrewId, crewFilterOptions])

const filtered = useMemo(() => {
  if (!canReadOvertime) return []

  return applyCrewingPresentationFilters(rows, {
    searchTerm: useCrewDropdownFilter ? undefined : searchTerm,
    selectedCrewId: useCrewDropdownFilter ? selectedCrewId : undefined,
    selectedVesselId,
    allowCompanyPresentationFilters: allowCompanyFilters,
    selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
    selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
  }, {
    vesselById,
    extraPredicate: (row) => {
      const typed = row
      const isDraftVisibleToUser =
        typed.status !== 'DRAFT' ||
        isSuperAdmin ||
        (isFillOthersOnlyCrewRank && canActOnRow(typed)) ||
        (isCrew && effectiveCurrentCrewId > 0 && typed.crewId === effectiveCurrentCrewId)
      const matchesStatus = statusFilter === 'All' || typed.status === statusFilter
      const matchesMonth = !monthFilter || (typed.overtimeMonth || '').startsWith(monthFilter)
      const term = searchTerm.trim().toLowerCase()
      const matchesCrew = !selectedCrewId || typed.crewId === Number(selectedCrewId)
      const matchesSearch =
        (typed.crewName || '').toLowerCase().includes(term) ||
        (typed.rankName || '').toLowerCase().includes(term)
      const hasCrewScope = !!(ownCompanyGroupId || ownCompanyAdminId || ownVesselId)
      const vessel = typed.vesselId ? vesselById.get(typed.vesselId) : undefined
      const companyGroupId = vessel
        ? ((vessel as any).companyGroupAdminId ??
          vessel.companyGroupAdmin?.id ??
          (vessel.companyAdmin as any)?.cgaid?.id ??
          vessel.companyAdmin?.cga?.id)
        : undefined
      const companyAdminId = vessel ? (vessel.companyAdmin?.id ?? (vessel as any).companyAdminId) : undefined
      const matchesOwnCompanyGroup = !ownCompanyGroupId || companyGroupId === ownCompanyGroupId
      const matchesOwnCompanyAdmin = !ownCompanyAdminId || companyAdminId === ownCompanyAdminId
      const matchesOwnVessel = !ownVesselId || typed.vesselId === ownVesselId
      const matchesRestrictedCrewScope =
        !isRestrictedCrewRank ||
        ((!hasCrewScope || (matchesOwnCompanyGroup && matchesOwnCompanyAdmin && matchesOwnVessel)) &&
          !isSelfOvertimeRow(typed))
      return (
        isDraftVisibleToUser &&
        matchesStatus &&
        matchesMonth &&
        matchesRestrictedCrewScope &&
        (useCrewDropdownFilter ? matchesCrew : matchesSearch)
      )
    },
  })
}, [
  rows,
  canReadOvertime,
  searchTerm,
  statusFilter,
  monthFilter,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  selectedCrewId,
  vesselById,
  allowCompanyFilters,
  useCrewDropdownFilter,
  isRestrictedCrewRank,
  ownCompanyGroupId,
  ownCompanyAdminId,
  ownVesselId,
  isSuperAdmin,
  isCrew,
  isFillOthersOnlyCrewRank,
  effectiveCurrentCrewId,
  crewSignOnDateByCrewId,
])

const totalPages = Math.ceil(filtered.length / rowsPerPage) || 1
const handleSort = (key: SortKey) => {
  setSortConfig((prev) => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
  }))
}

const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
  const container = event.currentTarget
  if (container.scrollWidth <= container.clientWidth) return

  // Keep vertical scrolling native; use Shift + wheel for horizontal intent.
  const isShiftHorizontalIntent = event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
  if (!isShiftHorizontalIntent || event.deltaY === 0) return

  const isScrollingRight = event.deltaY > 0
  const canScrollContainerRight = container.scrollLeft + container.clientWidth < container.scrollWidth - 1
  const canScrollContainerLeft = container.scrollLeft > 0
  if ((isScrollingRight && !canScrollContainerRight) || (!isScrollingRight && !canScrollContainerLeft)) return

  event.preventDefault()
  container.scrollLeft += event.deltaY
}

const sortedRows = useMemo(() => {
  const copy = [...filtered]
  copy.sort((first, second) => {
    let a: string | number = ''
    let b: string | number = ''

    if (sortConfig.key === 'srNo') {
      a = Number(first.srNo || 0)
      b = Number(second.srNo || 0)
    } else if (sortConfig.key === 'crewName') {
      a = (first.crewName || '').toLowerCase()
      b = (second.crewName || '').toLowerCase()
    } else {
      a = (first.rankName || '').toLowerCase()
      b = (second.rankName || '').toLowerCase()
    }

    if (typeof a === 'number' && typeof b === 'number') {
      return sortConfig.direction === 'asc' ? a - b : b - a
    }

    return sortConfig.direction === 'asc'
      ? String(a).localeCompare(String(b))
      : String(b).localeCompare(String(a))
  })
  return copy
}, [filtered, sortConfig])

const totalPagesSorted = Math.ceil(sortedRows.length / rowsPerPage) || 1
const start = (currentPage - 1) * rowsPerPage
const currentRows = sortedRows.slice(start, start + rowsPerPage)

useEffect(() => {
  if (currentPage > totalPagesSorted) {
    setCurrentPage(totalPagesSorted)
  }
}, [currentPage, totalPagesSorted])

const renderPagination = () => {
  const pages = []
  const showPages = 5
  let startPage = Math.max(1, currentPage - 2)
  let endPage = Math.min(totalPagesSorted, startPage + showPages - 1)

  if (endPage - startPage < showPages - 1) {
    startPage = Math.max(1, endPage - showPages + 1)
  }

  if (startPage > 1) {
    pages.push(
      <li key={1} className='page-item'>
        <button
          className='page-link text-muted'
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            padding: '8px 12px',
            fontSize: '14px',
            minWidth: '40px',
            borderRadius: '6px',
          }}
          onClick={() => setCurrentPage(1)}
        >
          1
        </button>
      </li>
    )

    if (startPage > 2) {
      pages.push(
        <li key='ellipsis1' className='page-item disabled'>
          <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>
            ...
          </span>
        </li>
      )
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(
      <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
        <button
          className='page-link text-muted'
          style={{
            backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
            border: '1px solid #dee2e6',
            padding: '8px 12px',
            fontSize: '14px',
            minWidth: '40px',
            borderRadius: '6px',
            outline: 'none',
            boxShadow: 'none',
          }}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      </li>
    )
  }

  if (endPage < totalPagesSorted) {
    if (endPage < totalPagesSorted - 1) {
      pages.push(
        <li key='ellipsis2' className='page-item disabled'>
          <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>
            ...
          </span>
        </li>
      )
    }

    pages.push(
      <li key={totalPagesSorted} className='page-item'>
        <button
          className='page-link text-muted'
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            padding: '8px 12px',
            fontSize: '14px',
            minWidth: '40px',
            borderRadius: '6px',
          }}
          onClick={() => setCurrentPage(totalPagesSorted)}
        >
          {totalPagesSorted}
        </button>
      </li>
    )
  }

  return pages
}

const openNew = async () => {
  const baseMonthKey = monthFilter || new Date().toISOString().slice(0, 7)
  const seedCrewId = isCrew && !isCrewMaster && !isFillOthersOnlyCrewRank
    ? Number(selfCrewMember?.id ?? currentCrewId ?? 0)
    : 0
  const crew = overtimeEligibleCrewMembers.find((c) => c.id === seedCrewId)
  const seedCrewSignOnMonth = toSignOnMonthKey(crew?.latestSignOnDate)
  const nowMonth = `${seedCrewSignOnMonth && seedCrewSignOnMonth > baseMonthKey ? seedCrewSignOnMonth : baseMonthKey}-01`
  let overtimeRate = 0
  let seedAccountCurrency: string | null = null
  let seedOtherCurrency: string | null = null
  if (seedCrewId) {
    const details = await getCrewLatestBankingDetails(seedCrewId)
    overtimeRate = Number(details?.overtimeRate ?? details?.overtimePay ?? 0)
    seedAccountCurrency = details?.salaryCurrency ?? null
    seedOtherCurrency = details?.salaryOtherCurrency ?? null
  }

  const rankId = crew?.rankId || null
  const seed: CrewOvertime = recalculate({
    id: 0,
    srNo: rows.length + 1,
    serialNo: '',
    crewId: seedCrewId,
    crewName: crew?.name || '',
    vesselId: crew?.vesselId || crew?.vessel?.id || null,
    vesselName: crew?.vessel?.fleet_name || null,
    rankId,
    rankName: resolveRankName(rankId),
    overtimeMonth: nowMonth,
    otHours: 0,
    maxOtAllowed: 109,
    basicWages: 0,
    salaryCurrency: seedAccountCurrency ?? null,
    salaryOtherCurrency: seedOtherCurrency ?? null,
    overtimeRate,
    otPerMonth: 0,
    otPerHour: 0,
    otAmount: 0,
    exceedAmount: 0,
    remarks: '',
    officeRemarks: null,
    status: 'DRAFT',
    submittedAt: null,
    reviewedAt: null,
    reviewedByName: null,
    rejectionReason: null,
    createdAt: '',
    updatedAt: '',
  })
  setEditing(seed)
  setIsNew(true)
  setModalOpen(true)
}

const onCrewSelected = async (crewId: number) => {
  if (!editing || !crewId) return
  const crew = modalEligibleCrewMembers.find((c) => c.id === crewId)
  if (!crew || !isOvertimeTargetCrewAllowed(crew)) {
    toast.error('Overtime is disabled for the selected crew rank.', { position: 'top-center' })
    return
  }
  const crewSignOnMonth = toSignOnMonthKey(crew?.latestSignOnDate)
  const currentMonthKey = (editing.overtimeMonth || '').slice(0, 7)
  const nextOvertimeMonth =
    crewSignOnMonth && (!currentMonthKey || currentMonthKey < crewSignOnMonth)
      ? `${crewSignOnMonth}-01`
      : editing.overtimeMonth
  const details = await getCrewLatestBankingDetails(crewId)
  const overtimeRate = Number(details?.overtimeRate ?? details?.overtimePay ?? 0)
  const rankId = crew?.rankId || null
  setEditing(
    recalculate({
      ...editing,
      crewId,
      crewName: crew?.name || '',
      vesselId: crew?.vesselId || crew?.vessel?.id || null,
      vesselName: crew?.vessel?.fleet_name || null,
      rankId,
      rankName: resolveRankName(rankId),
      overtimeMonth: nextOvertimeMonth,
      salaryCurrency: details?.salaryCurrency ?? null,
      salaryOtherCurrency: details?.salaryOtherCurrency ?? null,
      overtimeRate,
    })
  )
}

const save = async (submitAfterSave = false) => {
  if (!editing) return
  if (!isNew && !canEditRow(editing)) {
    toast.error('You are not allowed to edit this overtime row', { position: 'top-center' })
    return
  }
  if (isRestrictedCrewRank && editing.crewId === effectiveCurrentCrewId) {
    toast.error('You are not allowed to fill your own overtime', { position: 'top-center' })
    return
  }
  if (!editing.crewId) {
    toast.error('Please select crew member', { position: 'top-center' })
    return
  }

  const selectedCrew = modalCrewMembers.find((crew) => crew.id === editing.crewId)
  if (!selectedCrew || !isOvertimeTargetCrewAllowed(selectedCrew)) {
    toast.error('Overtime is disabled for the selected crew rank.', { position: 'top-center' })
    return
  }

  const editingMonthKey = (editing.overtimeMonth || '').slice(0, 7)
  const crewSignOnMonth = getCrewSignOnMonth(editing.crewId, modalSignOnDateByCrewId)
  if (crewSignOnMonth && editingMonthKey && editingMonthKey < crewSignOnMonth) {
    toast.error(`Overtime month cannot be before sign-on month (${crewSignOnMonth}).`, { position: 'top-center' })
    return
  }

  setIsSaving(true)
  try {
    let persistedRow: CrewOvertime | null = null
    if (isNew) {
      persistedRow = await createCrewOvertime({
        crewId: editing.crewId,
        vesselId: editing.vesselId ?? undefined,
        rankId: editing.rankId ?? undefined,
        overtimeMonth: editing.overtimeMonth,
        otHours: editing.otHours,
        overtimeRate: editing.overtimeRate ?? editing.otPerHour ?? undefined,
        dayWiseSlots: editing.dayWiseSlots ?? undefined,
        remarks: editing.remarks || '',
      })
    } else {
      persistedRow = await updateCrewOvertime(editing.id, {
        serialNo: editing.serialNo,
        vesselId: editing.vesselId ?? undefined,
        rankId: editing.rankId ?? undefined,
        overtimeMonth: editing.overtimeMonth,
        otHours: editing.otHours,
        overtimeRate: editing.overtimeRate ?? editing.otPerHour ?? undefined,
        dayWiseSlots: editing.dayWiseSlots ?? undefined,
        remarks: editing.remarks || '',
      })
    }

    if (submitAfterSave) {
      const rowToSubmit = persistedRow ?? editing
      if (rowToSubmit.status === 'DRAFT') {
        await submitCrewOvertime(rowToSubmit.id)
        toast.success('Saved and submitted for office review', { position: 'top-center' })
      } else {
        toast.success('Saved and updated in office review', { position: 'top-center' })
      }
    } else {
      toast.success(isNew ? 'Crew overtime created' : 'Crew overtime updated', { position: 'top-center' })
    }

    setModalOpen(false)
    setEditing(null)
    setIsNew(false)
    await fetchRows()
  } catch (error: any) {
    console.error('Error saving crew overtime:', error)
    showErrorToast(error, isNew ? 'Failed to create crew overtime' : 'Failed to save crew overtime', isNew ? 'create' : 'update')
  } finally {
    setIsSaving(false)
  }
}

const submit = async (row: CrewOvertime) => {
  if (!canSubmitOvertime) {
    toast.error('You do not have permission to submit overtime', { position: 'top-center' })
    return
  }
  if (!canActOnRow(row)) {
    toast.error('You are not allowed to submit your own overtime', { position: 'top-center' })
    return
  }
  try {
    await submitCrewOvertime(row.id)
    toast.success('Submitted for review', { position: 'top-center' })
    await fetchRows()
  } catch (error: any) {
    console.error('Error submitting crew overtime:', error)
    showErrorToast(error, 'Failed to submit crew overtime', 'save')
  }
}

const saveReviewRemarks = async (officeRemarks: string) => {
  if (!reviewing) return
  try {
    await updateCrewOvertime(reviewing.id, { officeRemarks })
    toast.success('Office remarks saved', { position: 'top-center' })
    setReviewing(null)
    await fetchRows()
  } catch (error: any) {
    console.error('Error saving review remarks:', error)
    showErrorToast(error, 'Failed to save review remarks', 'save')
  }
}

const approveReview = async (officeRemarks: string) => {
  if (!reviewing) return
  if (!canActOnRow(reviewing)) {
    toast.error('You are not allowed to review your own overtime', { position: 'top-center' })
    return
  }
  try {
    await updateCrewOvertime(reviewing.id, { officeRemarks })
    await reviewCrewOvertime(reviewing.id)
    toast.success('Overtime reviewed successfully', { position: 'top-center' })
    setReviewing(null)
    await fetchRows()
  } catch (error: any) {
    console.error('Error reviewing crew overtime:', error)
    showErrorToast(error, 'Failed to review crew overtime', 'save')
  }
}

const doDelete = async () => {
  if (!deleting) return
  if (!canDeleteOvertimeRows) {
    toast.error('You do not have permission to delete this overtime record', { position: 'top-center' })
    return
  }
  try {
    await deleteCrewOvertime(deleting.id)
    toast.success('Overtime deleted', { position: 'top-center' })
    setDeleting(null)
    await fetchRows()
  } catch (error: any) {
    console.error('Error deleting crew overtime:', error)
    showErrorToast(error, 'Failed to delete crew overtime', 'delete')
  }
}

const saveDayWiseOvertimeHours = async (
  row: CrewOvertime,
  overtimeHours: number,
  dayWiseSlots: Record<string, boolean[]>
) => {
  if (!canEditRow(row)) {
    toast.warn('You do not have permission to edit this overtime record.', { position: 'top-center' })
    return
  }

  try {
    await updateCrewOvertime(row.id, {
      serialNo: row.serialNo,
      vesselId: row.vesselId ?? undefined,
      rankId: row.rankId ?? undefined,
      overtimeMonth: row.overtimeMonth,
      otHours: overtimeHours,
      overtimeRate: row.overtimeRate ?? row.otPerHour ?? undefined,
      dayWiseSlots,
      remarks: row.remarks || '',
    })
    toast.success('Overtime changes saved', { position: 'top-center' })
    await fetchRows()
  } catch (error: any) {
    console.error('Error saving overtime changes:', error)
    showErrorToast(error, 'Failed to save overtime changes', 'update')
  }
}

const createDayWiseOvertime = async (
  overtimeMonth: string,
  overtimeHours: number,
  dayWiseSlots: Record<string, boolean[]>
) => {
  if (!canAddOvertime) {
    toast.warn('You are not allowed to create overtime rows.', { position: 'top-center' })
    return
  }

  if (!isCrew) {
    toast.warn('Only editable crew users can create overtime from View 2.', { position: 'top-center' })
    return
  }

  const crewId = isFillOthersOnlyCrewRank
    ? Number(selectedCrewId || 0)
    : Number(effectiveCurrentCrewId || 0)

  if (isFillOthersOnlyCrewRank && !crewId) {
    toast.warn('Please select a crew member before saving overtime in View 2.', { position: 'top-center' })
    return
  }

  if (!crewId) {
    toast.error('Unable to resolve crew member for creating overtime.', { position: 'top-center' })
    return
  }

  if (isFillOthersOnlyCrewRank && crewId === Number(effectiveCurrentCrewId || 0)) {
    toast.error('You cannot fill your own overtime.', { position: 'top-center' })
    return
  }

  const overtimeMonthKey = (overtimeMonth || '').slice(0, 7)
  const crewSignOnMonth = getCrewSignOnMonth(crewId, crewSignOnDateByCrewId)
  if (crewSignOnMonth && overtimeMonthKey && overtimeMonthKey < crewSignOnMonth) {
    toast.error(`Overtime month cannot be before sign-on month (${crewSignOnMonth}).`, { position: 'top-center' })
    return
  }

  const crew = overtimeEligibleCrewMembers.find((c) => c.id === crewId)
  if (!crew || !isOvertimeTargetCrewAllowed(crew)) {
    toast.error('Overtime is disabled for the selected crew rank.', { position: 'top-center' })
    return
  }
  try {
    const details = await getCrewLatestBankingDetails(crewId)
    const overtimeRate = Number(details?.overtimeRate ?? details?.overtimePay ?? 0)
    await createCrewOvertime({
      crewId,
      vesselId: crew?.vesselId ?? crew?.vessel?.id ?? undefined,
      rankId: crew?.rankId ?? undefined,
      overtimeMonth,
      otHours: overtimeHours,
      overtimeRate,
      dayWiseSlots,
      remarks: '',
    })
    toast.success('Overtime row created from View 2', { position: 'top-center' })
    await fetchRows()
  } catch (error: any) {
    console.error('Error creating overtime row from View 2:', error)
    showErrorToast(error, 'Failed to create overtime row from View 2', 'create')
  }
}

if (isOvertimePageHiddenForCrew) {
  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-body py-10 text-center text-muted'>
              Overtime page is not available for your rank.
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position='top-center' />
    </div>
  )
}

return (
  <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
    <div className='d-flex flex-column flex-column-fluid'>
      <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
        <div className='card'>
          <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
            <div>
              <h3 className='card-label text-dark fw-bold'>Crew Overtime</h3>
            </div>
            <div className='card-toolbar d-flex align-items-center gap-3'>
              <div className='btn-group btn-group-sm' role='group' aria-label='View switch'>
                <button
                  type='button'
                  className={`btn btn-sm ${activeView === 'view1' ? 'btn_primary' : 'btn-light border border-secondary'}`}
                  onClick={() => setActiveView('view1')}
                  title='List View'
                >
                  View 1
                </button>
                <button
                  type='button'
                  className={`btn btn-sm ${activeView === 'view2' ? 'btn_primary' : 'btn-light border border-secondary'}`}
                  onClick={() => setActiveView('view2')}
                  title='Monthly Summary'
                >
                  View 2
                </button>
              </div>
              {canAddOvertime && activeView === 'view1' ? (
                <button
                  type='button'
                  className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(29, 78, 216)',
                    borderColor: 'rgb(29, 78, 216)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={openNew}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <line x1='12' y1='5' x2='12' y2='19'></line>
                    <line x1='5' y1='12' x2='19' y2='12'></line>
                  </svg>
                  Add Overtime
                </button>
              ) : null}
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='mb-5'>
              <div className='d-flex flex-wrap gap-3'>
                {!hideStatusFilterForRole ? (
                  <div style={{ width: '220px', maxWidth: '100%' }}>
                    <select className='form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'All' | OvertimeStatus)}>
                      <option value='All'>All Status</option>
                      {canViewDrafts && <option value='DRAFT'>Draft</option>}
                      <option value='SUBMITTED'>Submitted</option>
                      <option value='REVIEWED'>Reviewed</option>
                    </select>
                  </div>
                ) : null}
                {activeView !== 'view2' && (
                  <div style={{ width: '220px', maxWidth: '100%' }}>
                    <input
                      type='month'
                      className='form-control'
                      value={monthFilter}
                      onChange={(e) => handleMonthFilterChange(e.target.value)}
                    />
                  </div>
                )}
                {showCrewFilter ? (
                  <div style={{ width: '260px', maxWidth: '100%' }}>
                    {useCrewDropdownFilter ? (
                      <select className='form-select' value={selectedCrewId} onChange={(e) => setSelectedCrewId(e.target.value ? Number(e.target.value) : '')}>
                        <option value=''>All Crew</option>
                        {crewFilterOptions.map((crew) => (
                          <option key={crew.id} value={crew.id}>{crew.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Search crew or rank'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            {activeView === 'view2' ? (
              <CrewOvertimeView2
                rows={filtered}
                canEdit={canEditOvertimeRows}
                canEditRow={canEditRow}
                onSaveDayWise={saveDayWiseOvertimeHours}
                onCreateDayWise={createDayWiseOvertime}
              />
            ) : (
              <div className='report-table table-responsive'>
                <div style={{ overflowX: 'auto' }} onWheel={handleTableHorizontalWheelScroll}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th className='text-center text-nowrap' style={{ minWidth: '70px' }}>
                          <div className='d-flex align-items-center justify-content-center text-nowrap'>
                            <span className='me-1'>Sr/No</span>
                            <button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('srNo')}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'srNo' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '180px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Name</span>
                            <button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('crewName')}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'crewName' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '170px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Rank</span>
                            <button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('rankName')}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'rankName' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '120px' }} className='text-nowrap'>OT Hrs</th>
                        <th style={{ minWidth: '130px' }} className='text-nowrap'>Max OT</th>
                        <th style={{ minWidth: '140px' }} className='text-nowrap'>Overtime Rate</th>
                        <th style={{ minWidth: '130px' }} className='text-nowrap'>OT / Month</th>
                        <th style={{ minWidth: '130px' }} className='text-nowrap'>OT / Hour</th>
                        <th style={{ minWidth: '140px' }} className='text-nowrap'>OT Amount</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }} className='text-nowrap'>Remarks</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }} className='text-nowrap'>Office Remarks</th>
                        <th style={{ minWidth: '120px' }} className='text-nowrap'>Status</th>
                        <th style={{ minWidth: '170px', textAlign: 'center' }} className='text-nowrap'>Actions</th>
                      </tr>
                    </thead>
                    <tbody className='table-body text-start'>
                      {isLoading ? (
                        <TableSkeleton rows={rowsPerPage || 10} cols={13} />
                      ) : currentRows.length === 0 ? (
                        <tr><td colSpan={13} className='text-center text-muted py-5'>No overtime records found for the selected filters.</td></tr>
                      ) : currentRows.map((row, idx) => (
                        <tr key={row.id || idx}>
                          <td className='text-center'>{row.srNo || (start + idx + 1)}</td>
                          <td>{row.crewName || '-'}</td>
                          <td>{row.rankName || '-'}</td>
                          <td>{row.otHours || 0}</td>
                          <td>{row.maxOtAllowed || 109}</td>
                          <td>{formatWithCurrencySymbol(row.overtimeRate ?? row.otPerHour ?? 0, row.salaryCurrency, row.salaryOtherCurrency, 2)}</td>
                          <td>{formatWithCurrencySymbol(row.otPerMonth || 0, row.salaryCurrency, row.salaryOtherCurrency, 2)}</td>
                          <td>{formatWithCurrencySymbol(row.otPerHour || 0, row.salaryCurrency, row.salaryOtherCurrency, 4)}</td>
                          <td className='fw-bold'>{formatWithCurrencySymbol(row.otAmount || 0, row.salaryCurrency, row.salaryOtherCurrency, 2)}</td>
                          <td>
                            <RemarksCell
                              text={row.remarks}
                              title='Crew Overtime Remarks'
                              subtitle={row.crewName || ''}
                              onOpenModal={(title, subtitle, text) => {
                                setRemarksModal({ visible: true, title, subtitle, text })
                              }}
                            />
                          </td>
                          <td>
                            <RemarksCell
                              text={row.officeRemarks}
                              title='Office Remarks'
                              subtitle={row.crewName || ''}
                              onOpenModal={(title, subtitle, text) => {
                                setRemarksModal({ visible: true, title, subtitle, text })
                              }}
                            />
                          </td>
                          <td className='text-nowrap'>{defaultStatusBadge(row)}</td>
                          <td className='text-nowrap'>
                            <div className='d-flex gap-1'>
                              <button className='btn btn-icon btn-sm' title='View' onClick={() => setViewing(recalculate({ ...row }))}>
                                <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                              </button>

                              {canEditRow(row) ? (
                                <button className='btn btn-icon btn-sm' title='Edit' onClick={() => { setEditing(recalculate({ ...row })); setIsNew(false); setModalOpen(true) }}>
                                  <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                </button>
                              ) : null}

                              {canDeleteOvertimeRows && row.status === 'DRAFT' && canActOnRow(row) ? (
                                <button className='btn btn-icon btn-sm' title='Delete' onClick={() => setDeleting(row)}>
                                  <KTSVG path='/media/map/red-cross-icon.svg' className='svg-icon-3' />
                                </button>
                              ) : null}

                              {canSubmitOvertime && row.status === 'DRAFT' && canActOnRow(row) ? (
                                <button className='btn btn-light-primary btn-sm' onClick={() => submit(row)}>Submit</button>
                              ) : null}

                              {canReviewRow(row) ? (
                                <button className='btn btn-light-warning btn-sm' onClick={() => setReviewing(row)}>Office Review</button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                  style={{ position: 'static', bottom: 0, backgroundColor: '#fff', zIndex: 10 }}
                >
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <select
                      className='form-select'
                      style={{ borderRadius: '20px', width: '70px', border: '1px solid #dee2e6', fontSize: '14px', padding: '4px 8px' }}
                      value={rowsPerPage}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                      Showing <strong>{sortedRows.length ? start + 1 : 0}-{Math.min(start + rowsPerPage, sortedRows.length)}</strong> of <strong>{sortedRows.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', padding: '8px 12px', fontSize: '14px', borderRadius: '6px' }}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>
                        {renderPagination()}
                        <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', padding: '8px 12px', fontSize: '14px', borderRadius: '6px' }}
                            onClick={() => setCurrentPage((p) => Math.min(totalPagesSorted, p + 1))}
                            disabled={currentPage >= totalPagesSorted}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    <CrewOvertimeModal
      visible={modalOpen}
      overtime={editing}
      isNew={isNew}
      isSaving={isSaving}
      crewMembers={modalSelectableCrewMembers}
      lockCrewSelection={isCrew && !isCrewMaster && !isFillOthersOnlyCrewRank}
      canEdit={canEditOvertimeRows}
      canSubmit={!!editing && (isNew || editing.status === 'DRAFT')}
      onClose={() => { setModalOpen(false); setEditing(null); setIsNew(false) }}
      onChange={(next) => setEditing(recalculate(next))}
      onCrewSelected={onCrewSelected}
      onSave={() => save(false)}
      onSubmit={() => save(true)}
      crewSignOnDateById={modalSignOnDateByCrewId}
    />

    <ViewCrewOvertimeModal visible={!!viewing} overtime={viewing} onClose={() => setViewing(null)} />

    <CrewOvertimeReviewModal
      visible={!!reviewing}
      overtime={reviewing}
      isLoading={isSaving}
      onClose={() => setReviewing(null)}
      onSaveRemarks={saveReviewRemarks}
      onApprove={approveReview}
    />

    <DeleteCrewOvertimeModal
      visible={!!deleting}
      onClose={() => setDeleting(null)}
      onConfirm={doDelete}
    />

    <RemarksModal
      visible={remarksModal.visible}
      title={remarksModal.title}
      subtitle={remarksModal.subtitle}
      remarksText={remarksModal.text}
      onClose={() => setRemarksModal(prev => ({ ...prev, visible: false }))}
    />

    <ToastContainer position='top-center' />
  </div>
)
}

export default CrewOvertimePage
